/**
 * API-based radar repository implementation
 * Uses the API client from ADR-019 to interact with backend services
 */
import { RadarRepositoryPort } from '../../application/ports/RadarRepositoryPort';
import { Radar, Practice } from '../../domain/entities/RadarTypes';
import { apiClient } from '../api/apiClient';
import { validateRadar } from '../../domain/schemas/RadarSchemas';
import { DomainError, NotFoundError } from '../../domain/errors/DomainErrors';

/**
 * Repository implementation for radar data using API endpoints
 */
export class ApiRadarRepository implements RadarRepositoryPort {
  /**
   * Base API path for radar operations
   */
  private readonly basePath = '/radars';

  /**
   * Create a new radar
   */
  async createRadar(
    tenantId: string,
    teamId: string,
    radar: Omit<Radar, 'id' | 'tenantId' | 'teamId' | 'createdAt' | 'updatedAt'>
  ): Promise<Radar> {
    try {
      const newRadar = {
        ...radar,
        tenantId,
        teamId,
      };
      const data = await apiClient.post<unknown>(this.basePath, newRadar);
      return this.ensureRadarFormat(data);
    } catch (error) {
      this.handleApiError(error, `Failed to create radar ${radar.name}`);
      throw error; // This line will never be reached due to handleApiError always throwing
    }
  }

  /**
   * Update an existing radar
   */
  async updateRadar(
    tenantId: string,
    teamId: string,
    id: string,
    radar: Partial<Radar>
  ): Promise<Radar> {
    try {
      const data = await apiClient.put<unknown>(`${this.basePath}/${id}`, {
        ...radar,
        tenantId,
        teamId,
      });
      return this.ensureRadarFormat(data);
    } catch (error) {
      this.handleApiError(error, `Failed to update radar with ID ${id}`);
      throw error; // This line will never be reached due to handleApiError always throwing
    }
  }

  /**
   * Find a radar by its ID
   */
  async findById(
    tenantId: string,
    teamId: string,
    id: string
  ): Promise<Radar | null> {
    try {
      const params: Record<string, unknown> = { tenantId, teamId };
      const data = await apiClient.get<unknown>(
        `${this.basePath}/${id}`,
        params
      );
      return this.ensureRadarFormat(data);
    } catch (error) {
      if (this.isNotFoundError(error)) {
        return null;
      }
      this.handleApiError(error, `Failed to find radar with ID ${id}`);
      return null; // This line will never be reached due to handleApiError always throwing
    }
  }

  /**
   * Find all radars for a team
   */
  async findAllByTeam(tenantId: string, teamId: string): Promise<Radar[]> {
    try {
      const params: Record<string, unknown> = { tenantId, teamId };
      const data = await apiClient.get<unknown[]>(this.basePath, params);

      if (!Array.isArray(data)) {
        return [];
      }

      return data.map(item => this.ensureRadarFormat(item));
    } catch (error) {
      this.handleApiError(
        error,
        `Failed to find radars for tenant ${tenantId}, team ${teamId}`
      );
      return []; // This line will never be reached due to handleApiError always throwing
    }
  }

  /**
   * Delete a radar by ID
   */
  async deleteRadar(
    tenantId: string,
    teamId: string,
    id: string
  ): Promise<void> {
    try {
      // We need to add query params since apiClient.delete doesn't accept a second body parameter
      const url = `${this.basePath}/${id}?tenantId=${encodeURIComponent(tenantId)}&teamId=${encodeURIComponent(teamId)}`;
      await apiClient.delete(url);
    } catch (error) {
      if (!this.isNotFoundError(error)) {
        this.handleApiError(error, `Failed to delete radar with ID ${id}`);
      }
    }
  }

  /**
   * Add a practice to a radar
   */
  async addPractice(
    tenantId: string,
    teamId: string,
    radarId: string,
    practice: Omit<Practice, 'id'>
  ): Promise<Radar> {
    try {
      // Add tenant and team info to the URL as query params
      const url = `${this.basePath}/${radarId}/practices?tenantId=${encodeURIComponent(tenantId)}&teamId=${encodeURIComponent(teamId)}`;
      const data = await apiClient.post<unknown>(url, practice);
      return this.ensureRadarFormat(data);
    } catch (error) {
      this.handleApiError(error, `Failed to add practice to radar ${radarId}`);
      throw error; // This line will never be reached due to handleApiError always throwing
    }
  }

  /**
   * Update a practice in a radar
   */
  async updatePractice(
    tenantId: string,
    teamId: string,
    radarId: string,
    practiceId: string,
    updates: Partial<Practice>
  ): Promise<Radar> {
    try {
      // Add tenant and team info to the URL as query params
      const url = `${this.basePath}/${radarId}/practices/${practiceId}?tenantId=${encodeURIComponent(tenantId)}&teamId=${encodeURIComponent(teamId)}`;
      const data = await apiClient.put<unknown>(url, updates);
      return this.ensureRadarFormat(data);
    } catch (error) {
      this.handleApiError(
        error,
        `Failed to update practice ${practiceId} in radar ${radarId}`
      );
      throw error; // This line will never be reached due to handleApiError always throwing
    }
  }

  /**
   * Delete a practice from a radar
   */
  async deletePractice(
    tenantId: string,
    teamId: string,
    radarId: string,
    practiceId: string
  ): Promise<void> {
    try {
      // Add tenant and team info to the URL as query params
      const url = `${this.basePath}/${radarId}/practices/${practiceId}?tenantId=${encodeURIComponent(tenantId)}&teamId=${encodeURIComponent(teamId)}`;
      await apiClient.delete(url);
    } catch (error) {
      if (!this.isNotFoundError(error)) {
        this.handleApiError(
          error,
          `Failed to delete practice ${practiceId} from radar ${radarId}`
        );
      }
    }
  }

  /**
   * Ensure the API response matches the expected Radar format
   * This provides additional guarantees beyond validateRadar
   */
  private ensureRadarFormat(data: unknown): Radar {
    try {
      // First validate the data with the schema
      const validatedData = validateRadar(data) as Record<string, unknown>;

      // Create a new object that conforms to Radar interface
      const radar: Radar = {
        id: String(validatedData.id || ''),
        tenantId: String(validatedData.tenantId || ''),
        teamId: String(validatedData.teamId || ''),
        name: String(validatedData.name || ''),
        description: String(validatedData.description || ''),
        practices: Array.isArray(validatedData.practices)
          ? (validatedData.practices as Practice[])
          : [],
        createdAt:
          validatedData.createdAt instanceof Date
            ? validatedData.createdAt
            : new Date((validatedData.createdAt as string) || Date.now()),
        updatedAt:
          validatedData.updatedAt instanceof Date
            ? validatedData.updatedAt
            : new Date((validatedData.updatedAt as string) || Date.now()),
      };

      return radar;
    } catch (error) {
      console.error('Error ensuring radar format:', error);
      // Create a minimal valid radar to avoid crashing
      return {
        id: 'error',
        tenantId: 'error',
        teamId: 'error',
        name: 'Error loading radar',
        description: 'There was an error loading this radar',
        practices: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    }
  }

  /**
   * Check if an error is a 404 Not Found error
   * @param error Error to check
   * @returns Boolean indicating if it's a not found error
   */
  private isNotFoundError(error: unknown): boolean {
    const apiError = error as { response?: { status?: number } };
    return apiError.response?.status === 404;
  }

  /**
   * Handle API errors by converting them to domain errors
   * @param error Error from API
   * @param message Human-readable error message
   * @throws Appropriate domain error
   */
  private handleApiError(error: unknown, message: string): never {
    const apiError = error as {
      response?: {
        status?: number;
        data?: {
          message?: string;
          errors?: string[];
        };
      };
    };

    // Get detailed error message from response if available
    const detailedMessage = apiError.response?.data?.message || message;
    const errorInfo: Record<string, unknown> = {};

    if (Array.isArray(apiError.response?.data?.errors)) {
      errorInfo.errors = apiError.response?.data?.errors;
    }

    switch (apiError.response?.status) {
      case 400:
        throw new DomainError(detailedMessage, errorInfo);
      case 404:
        throw new NotFoundError(detailedMessage);
      default:
        throw new DomainError(detailedMessage);
    }
  }
}
